/**
 * @fileoverview State choices query hook.
 * @description The states and union territories the Add and Edit User forms offer, from the
 * Tenants prospect metadata (GET /tenants/prospect-metadata). It is the same request, and the same
 * query, as the Sales pipeline's metadata (shared service and key), narrowed to the states, so the
 * catalogue is fetched once between them. It rarely changes, so a read stays fresh for five
 * minutes.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQuery } from '@tanstack/react-query'

import { getProspectMetadata } from '@/shared/api/directory-options.service'
import { PROSPECT_METADATA_QUERY_KEY } from '@/shared/constants/directory-options-query-keys.constants'

import type { StateChoice } from '@/features/users/types/users.types'
import type { ApiError } from '@/shared/types/http.types'
import type { ProspectMetadata } from '@/shared/types/prospect-metadata.types'

const FIVE_MINUTES_MS = 5 * 60 * 1000

/**
 * @description Narrows the prospect metadata to the states a user may be given. A user with no
 * state is in All India, which has no catalogue entry.
 * @param {ProspectMetadata} metadata - The prospect metadata catalogues.
 * @returns {StateChoice[]} Every state, in the catalogue's order.
 */
function selectStateChoices(metadata: ProspectMetadata): StateChoice[] {
  return metadata.states.map(({ id, name }) => ({ stateId: id, name }))
}

/**
 * @description Fetches the states a user may be given.
 * @returns {import('@tanstack/react-query').UseQueryResult<StateChoice[], ApiError>} The query
 * result.
 */
export function useStateChoices() {
  return useQuery<ProspectMetadata, ApiError, StateChoice[]>({
    queryKey: PROSPECT_METADATA_QUERY_KEY,
    queryFn: getProspectMetadata,
    select: selectStateChoices,
    staleTime: FIVE_MINUTES_MS,
  })
}
